import { environment } from 'src/environments/environment';
import { CardsService } from 'src/app/shared/providers/cards.service';
import { Component, EventEmitter, OnInit, Output, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { PROFILE_REGION } from 'src/app/models/user';
import { UserDataService } from 'src/app/shared/providers/user-data.service';
import { WebNavService } from '../webnavigation.service';
import { trigger, style, transition, animate } from '@angular/animations';
import { INavpanelAction, NAVPANEL_ACTION_TYPE, SUBSCRIPTION_STATUS } from 'src/app/models/card';
import { FirebaseAuthService } from 'src/app/shared/providers/firebase-auth.service';
import { UtilityFunctionsService } from 'src/app/shared/providers/utility-functions.service';
import { Subject, throttleTime, Subscription } from 'rxjs';
import { FeedbackService } from 'src/app/shared/providers/feedback.service';
import { UploadFileService } from 'src/app/shared/providers/upload-file.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-webaccount',
  templateUrl: './webaccount.component.html',
  styleUrls: ['./webaccount.component.scss'],
  animations: [
    trigger('slideToggle', [
      transition(':enter', [
        style({ height: 0, opacity: 0, overflow: 'hidden' }), // Start with no height and transparent
        animate('0.2s ease-out', style({ height: '*', opacity: 1 })) // Animate to the natural height and full opacity
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, 'padding-bottom': '0px',  overflow: 'hidden' }), // Start with current height and full opacity
        animate('0.2s ease-out', style({ height: 0, opacity: 0, 'padding-bottom': '0px' })) // Animate to no height and transparent
      ])
    ])
  ]
})
export class WebaccountComponent implements OnInit, OnDestroy {
  @Output() webAccountPopupEvent = new EventEmitter<INavpanelAction>();

  Math = Math; // Expose Math to template
  isAccountRegionShown: boolean;
  environment = environment;

  PROFILE_REGION = PROFILE_REGION;
  isRenderLimitRegionShown: boolean;

  // Gated on the Firebase `admin` custom claim, not on currentUser.isAdmin
  // (that flag comes from subscription_status and drives the moderation UI).
  isContentAdmin = false;

  private renderLimitChangeSubject = new Subject<number>();
  private userDataSubscription: Subscription;

  constructor(
    public cardService: CardsService,
    public userDataService: UserDataService,
    public webNavService: WebNavService,
    public firebaseAuth: FirebaseAuthService,
    public cardsService: CardsService,
    public utilityService: UtilityFunctionsService,
    private dialogRef: MatDialogRef<WebaccountComponent>,
    public feedbackService: FeedbackService,
    public uploadFileService: UploadFileService,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {

  }

  ngOnInit(): void {
    this.firebaseAuth.hasAdminClaim().then((isContentAdmin) => {
      this.isContentAdmin = isContentAdmin;
      this.cdr.detectChanges();
    });

    // Subscribe to user data updates to refresh UI when subscription data is loaded
    this.userDataSubscription = this.firebaseAuth.userDataUpdated.subscribe(() => {
      this.cdr.detectChanges();
    });

    this.renderLimitChangeSubject.pipe(
      throttleTime(400)
    ).subscribe((renderLimit) => {
      this.updateRenderLimit(renderLimit);
    });
  }

  public deleteAccClick() {
    this.webNavService.deleteAccRequest();
  }

  public cancelSub() {
    console.log('cancelSub clicked')
    this.firebaseAuth.isSubscriptionRelatedBtnDisabled = true;
    this.cardsService.stripeCancelSubscription();
  }

  public isSubscriber(): boolean {
    return this.firebaseAuth.currentUser?.subscriptionStatus === SUBSCRIPTION_STATUS.SUBSCRIBER;
  }

  public isExpired(): boolean {
    return this.firebaseAuth.currentUser?.subscriptionStatus === SUBSCRIPTION_STATUS.EXPIRED;
  }

  public isTrial(): boolean {
    return this.firebaseAuth.currentUser?.subscriptionStatus === SUBSCRIPTION_STATUS.TRIAL;
  }

  public isTrialExpired(): boolean {
    return this.firebaseAuth.currentUser?.subscriptionStatus === SUBSCRIPTION_STATUS.TRIAL_EXPIRED;
  }

  public getUploadsLeft(): number {
    const uploadNumberLimit = this.firebaseAuth.currentUser?.uploadNumberLimit;
    const uploadsNumber = this.firebaseAuth.currentUser?.uploadsNumber;

    // Validate that we have valid numbers
    const limit = typeof uploadNumberLimit === 'number' && !isNaN(uploadNumberLimit) && uploadNumberLimit >= 0 ? uploadNumberLimit : 0;
    const usedFromBackend = typeof uploadsNumber === 'number' && !isNaN(uploadsNumber) && uploadsNumber >= 0 ? uploadsNumber : 0;

    const currentlyProcessing = this.uploadFileService.currentNumberOfUploadingOrRenderingModels;
    const actualUsed = Math.max(usedFromBackend, currentlyProcessing);

    return Math.max(0, limit - actualUsed);
  }

  public shouldShowRenderLimitSelector(): boolean {
    // Show selector only if user is subscriber and reached or is close to the limit
    if (!this.isSubscriber()) {
      return false;
    }
    const renderModelsPerCycle = this.firebaseAuth.currentUser?.renderModelsPerCycle;
    const renderLimit = this.firebaseAuth.currentUser?.renderLimit;

    // Don't show if render limit data is not available (null or undefined)
    if (renderLimit == null || renderModelsPerCycle == null) {
      return false;
    }

    // Show if 80% or more of the limit is used
    return renderModelsPerCycle >= renderLimit * 0.8;
  }

  public subscribe() {
    setTimeout(() => {
    this.webAccountPopupEvent.emit({ actionName: 'Subscribe', actionType: NAVPANEL_ACTION_TYPE.SHOW_SUBSCRIBE });
    }, 350);
    this.dialogRef.close();
  }

  public renew() {
    console.log('renew subscription clicked')
    this.firebaseAuth.isSubscriptionRelatedBtnDisabled = true;
    this.cardsService.stripeRenewSubscription();
  }

  public betaSettings() {
    if(environment.production) return;
    setTimeout(() => {
      this.webAccountPopupEvent.emit({ actionName: 'Settings', actionType: NAVPANEL_ACTION_TYPE.SETTINGS });
      }, 350);
      this.dialogRef.close();
  }

  public signOut() {
    console.log('signOut')
    this.webNavService.signOut();
    this.dialogRef.close();
  }

  public openTermsOfService() {
    // Navigate to Terms of Service page or open in new tab
    window.open('/legal/terms-of-service', '_blank');
  }

  public openPrivacyPolicy() {
    // Navigate to Privacy Policy page or open in new tab
    window.open('/legal/privacy-policy', '_blank');
  }

  public openContentAdmin(): void {
    this.dialogRef.close();
    setTimeout(() => {
      this.router.navigate(['/admin/content']);
    }, 120);
  }

  public getFormattedEndDate(): string {
    const subscriptionEndDate = new Date(this.firebaseAuth.currentUser.subscriptionEndDateTime);
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return subscriptionEndDate.toLocaleDateString('en-US', options);
  }

  public getSubscriptionRenewalDate(): string {
    if (!this.firebaseAuth.currentUser || !this.firebaseAuth.currentUser.subscriptionEndDateTime) {
      return null;
    }

    const subscriptionEndDate = new Date(this.firebaseAuth.currentUser.subscriptionEndDateTime);
    subscriptionEndDate.setDate(subscriptionEndDate.getDate() + 1);

    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return subscriptionEndDate.toLocaleDateString('en-US', options);
  }

  public toggleRegion(event: Event, region: string) {
    console.log('region click')
    event.stopPropagation();
    switch (region) {
      case PROFILE_REGION.PERSONAL:
        this.isAccountRegionShown = !this.isAccountRegionShown;
        this.isRenderLimitRegionShown = false;
        break;
      case PROFILE_REGION.SUBSCRIPT:
        this.isAccountRegionShown = false;
        this.isRenderLimitRegionShown = false;
        break;
      case PROFILE_REGION.RENDER_LIMIT:
        this.isRenderLimitRegionShown = !this.isRenderLimitRegionShown;
        this.isAccountRegionShown = false;
        break;
      default:
        break;
    }
    return
  }

  public onRenderLimitChanged(renderLimit: number): void {
    this.renderLimitChangeSubject.next(renderLimit);
  }

  public submitFeedback(): void {
    if (!this.feedbackService.feedbackText.trim() || this.feedbackService.isSubmitting) {
      return;
    }

    this.feedbackService.submitFeedback()
      .subscribe({
        next: (success) => {
          if (success) {
            this.utilityService.showImportantSnackBar('Your feedback sent. We will reply shortly.', 200);
            this.feedbackService.resetFeedback();
          } else {
            this.utilityService.showImportantErrorSnackBar('Sorry, seems something went wrong; Contact us support@3dway.io', 100);
          }
        },
        error: (error) => {
          console.error('Error submitting feedback:', error);
          this.utilityService.showImportantErrorSnackBar('Failed to send feedback. Please check your connection and try again.', 0);
        }
      });
  }

  private updateRenderLimit(renderLimit: number): void {
    this.firebaseAuth.currentUser.renderLimit = renderLimit;
    this.cardService.setRenderLimit(this.firebaseAuth.currentUser.uid, this.firebaseAuth.currentUser.renderLimit)
      .then((resp) => {
        console.log('Billing protection value updated', resp);
      })
      .catch((err) => {
        this.utilityService.showImportantErrorSnackBar('Something went wrong while updating billing protection value', 0);
        console.error(err);
      });
  }

  public isSubscriptionEndLabelShown(): boolean {
    return !this.firebaseAuth.isSubscriptionRelatedBtnDisabled &&
            this.firebaseAuth.currentUser?.subscriptionStatus &&
            this.firebaseAuth.currentUser?.subscriptionEndDateTime &&
           !this.firebaseAuth.currentUser?.isAutoRenewDisabled &&
            this.firebaseAuth.currentUser?.subscriptionStatus === SUBSCRIPTION_STATUS.SUBSCRIBER;
  }


  ngOnDestroy(): void {
    if (this.userDataSubscription) {
      this.userDataSubscription.unsubscribe();
    }
  }

}
